import {
  TextInput,
  Textarea,
  Stack,
  Select,
  Switch,
  Text,
  Alert,
  Button,
  Group,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useMemo } from "react";
import { IconAlertCircle, IconExternalLink } from "@tabler/icons-react";
import { AccountReportDelivery } from "../../../services/admin/accountreportdelivery.service";
import { usePracticePhysicians } from "../../../hooks/admin/usePractices";
import { usePortalAccessSubjectIdSet } from "../../../hooks/admin/usePortalAccess";
import {
  scrollToFirstError,
  required,
  email,
  phone,
  fax,
} from "../../../utils/formValidation";

export interface AccountReportDeliveryFormValues {
  practiceId: string;
  reportDeliveryMethodId: string;
  physicianId: string;
  email: string;
  phone: string;
  fax: string;
  address: string;
  isActive: boolean;
  remoteConnectionIdentifier: string;
}

interface AccountReportDeliveryFormProps {
  initialValues?: AccountReportDelivery;
  onSubmit: (values: AccountReportDeliveryFormValues) => void;
  practices: Array<{ value: string; label: string }>;
  deliveryMethods: Array<{ value: string; label: string }>;
}

function getMethodName(
  methodId: string,
  methods: Array<{ value: string; label: string }>
): string {
  const method = methods.find((m) => m.value === methodId);
  return method?.label?.toLowerCase() || "";
}

export default function AccountReportDeliveryForm({
  initialValues,
  onSubmit,
  practices,
  deliveryMethods,
}: AccountReportDeliveryFormProps) {
  const form = useForm<AccountReportDeliveryFormValues>({
    validateInputOnBlur: true,
    initialValues: {
      practiceId: initialValues?.practiceId || "",
      reportDeliveryMethodId: initialValues?.reportDeliveryMethodId || "",
      physicianId: "",
      email: initialValues?.email || "",
      phone: initialValues?.phone || "",
      fax: initialValues?.fax || "",
      address: initialValues?.address || "",
      isActive: initialValues?.isActive ?? true,
      remoteConnectionIdentifier:
        initialValues?.remoteConnectionIdentifier || "",
    },
    validate: (values) => {
      const methodName = getMethodName(
        values.reportDeliveryMethodId,
        deliveryMethods
      );

      let emailError: string | null = null;
      if (methodName === "email" || methodName.startsWith("portal")) {
        emailError =
          required("Email is required")(values.email) || email()(values.email);
      } else {
        emailError = email()(values.email);
      }

      let faxError: string | null = null;
      if (methodName === "fax") {
        faxError =
          required("Fax number is required")(values.fax) || fax()(values.fax);
      } else {
        faxError = fax()(values.fax);
      }

      let addressError: string | null = null;
      if (methodName === "mail" || methodName === "ship physical copy") {
        addressError = required("Mailing address is required")(values.address);
      }

      let remoteConnectionIdentifierError: string | null = null;
      if (
        methodName.includes("remote print") &&
        !values.remoteConnectionIdentifier?.trim()
      ) {
        remoteConnectionIdentifierError =
          "Connection identifier is required for Remote Print delivery";
      }

      return {
        practiceId: required("Practice is required")(values.practiceId),
        reportDeliveryMethodId: required("Delivery method is required")(
          values.reportDeliveryMethodId
        ),
        email: emailError,
        fax: faxError,
        address: addressError,
        phone: phone()(values.phone),
        remoteConnectionIdentifier: remoteConnectionIdentifierError,
      };
    },
  });

  const handleSubmit = form.onSubmit(
    useCallback(
      (values: AccountReportDeliveryFormValues) => onSubmit(values),
      [onSubmit]
    ),
    useCallback(() => scrollToFirstError(form), [form])
  );

  const { data: practicePhysicians = [] } = usePracticePhysicians(
    form.values.practiceId || null
  );

  const physicianOptions = useMemo(
    () =>
      practicePhysicians.map((pp) => ({
        value: pp.physician.id,
        label: `${pp.physician.lastName}, ${pp.physician.firstName}`,
      })),
    [practicePhysicians]
  );

  const handlePracticeChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("practiceId", value || "");
      form.setFieldValue("physicianId", "");
    },
    [form]
  );

  const selectedMethodName = useMemo(
    () => getMethodName(form.values.reportDeliveryMethodId, deliveryMethods),
    [form.values.reportDeliveryMethodId, deliveryMethods]
  );

  const showEmailField = useMemo(
    () =>
      selectedMethodName === "email" || selectedMethodName.startsWith("portal"),
    [selectedMethodName]
  );
  const showFaxField = useMemo(
    () => selectedMethodName === "fax",
    [selectedMethodName]
  );
  const showAddressField = useMemo(
    () =>
      selectedMethodName === "mail" ||
      selectedMethodName === "ship physical copy",
    [selectedMethodName]
  );
  const showRemotePrintField = useMemo(
    () => selectedMethodName.includes("remote print"),
    [selectedMethodName]
  );
  const showPhoneField = useMemo(
    () =>
      selectedMethodName.startsWith("ship") || selectedMethodName === "mail",
    [selectedMethodName]
  );
  const showNoConfigMessage = useMemo(
    () => !!selectedMethodName && selectedMethodName.startsWith("hl7"),
    [selectedMethodName]
  );

  // The backend rejects a Portal delivery method when the practice has no
  // active/invited portal grant. Surface that here, while the form is still
  // open, instead of letting the save fail — the user otherwise has to leave,
  // invite a contact, and come back to find out (OBS-110).
  const {
    subjectIdSet: practicesWithPortalAccess,
    isLoading: portalAccessLoading,
  } = usePortalAccessSubjectIdSet("practice");

  const needsPortalContact = useMemo(
    () =>
      selectedMethodName.startsWith("portal") &&
      !!form.values.practiceId &&
      !portalAccessLoading &&
      !practicesWithPortalAccess.has(form.values.practiceId),
    [
      selectedMethodName,
      form.values.practiceId,
      portalAccessLoading,
      practicesWithPortalAccess,
    ]
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <Select
          label="Practice"
          placeholder="Select practice"
          withAsterisk
          searchable
          data={practices}
          name="practiceId"
          value={form.values.practiceId || null}
          onChange={handlePracticeChange}
          error={form.errors.practiceId}
        />

        <Select
          label="Delivery Method"
          placeholder="Select delivery method"
          withAsterisk
          searchable
          data={deliveryMethods}
          name="reportDeliveryMethodId"
          {...form.getInputProps("reportDeliveryMethodId")}
        />

        {needsPortalContact && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
            title="This practice has no portal contact yet"
            role="alert"
          >
            <Stack gap="xs">
              <Text size="sm">
                Portal delivery needs at least one invited or active portal
                contact for this practice. Invite one, then come back and save —
                this form stays open.
              </Text>
              <Group>
                <Button
                  size="xs"
                  variant="light"
                  component="a"
                  href="/admin/portal-access"
                  target="_blank"
                  rel="noopener noreferrer"
                  rightSection={<IconExternalLink size={14} />}
                >
                  Invite a portal contact
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {showEmailField && (
          <TextInput
            label="Email Address"
            placeholder="Enter email address"
            type="email"
            withAsterisk
            name="email"
            {...form.getInputProps("email")}
          />
        )}

        {showEmailField && selectedMethodName.startsWith("portal") && (
          <Select
            label="Physician (optional)"
            description="Send portal invitation to this physician — leave blank to invite all physicians linked to this practice"
            placeholder={
              form.values.practiceId
                ? "Select a physician..."
                : "Select a practice first"
            }
            clearable
            searchable
            disabled={!form.values.practiceId}
            data={physicianOptions}
            {...form.getInputProps("physicianId")}
          />
        )}

        {showFaxField && (
          <TextInput
            label="Fax Number"
            placeholder="Enter fax number"
            withAsterisk
            name="fax"
            {...form.getInputProps("fax")}
          />
        )}

        {showAddressField && (
          <Textarea
            label="Mailing Address"
            placeholder="Enter mailing address"
            minRows={2}
            withAsterisk
            name="address"
            {...form.getInputProps("address")}
          />
        )}

        {showRemotePrintField && (
          <TextInput
            label="Connection Identifier"
            placeholder="e.g. clinic-printer-001"
            description="Must match the identifier configured in the Remote Print client app at the clinic"
            withAsterisk
            name="remoteConnectionIdentifier"
            {...form.getInputProps("remoteConnectionIdentifier")}
          />
        )}

        {showPhoneField && (
          <TextInput
            label="Phone (optional)"
            placeholder="Enter phone number"
            name="phone"
            {...form.getInputProps("phone")}
          />
        )}

        {showNoConfigMessage && (
          <Text size="sm" c="dimmed">
            No additional configuration required for this delivery method.
          </Text>
        )}

        <Switch
          label="Active"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
